export const DIR_TYPES = [
  { value: 'dir', label: '本地目录', icon: 'md-folder' },
  { value: 'remote', label: '远程目录', icon: 'md-cloud' },
  { value: 'ref', label: '引用目录', icon: 'lianjie' },
];

export const DIR_DATA_TYPES = [
  { value: 'raw', label: '原件目录', desc: '支持所有类型', icon: 'md-folder', creatable: true },
  { value: 'parsed', label: '文档目录', desc: '仅 Markdown', icon: 'md-document', creatable: true },
  { value: 'data_source', label: '数据源', desc: '绑定数据接口', icon: 'md-git-network', creatable: true },
  { value: 'rag', label: '向量(RAG)', desc: '向量检索', icon: 'md-pulse', creatable: false },
];

export const DIR_VIEW_FILTERS = [
  { value: 'all', label: '全部', short: '全', icon: 'md-apps' },
  { value: 'parsed', label: '文档目录', short: '文', icon: 'md-document' },
  { value: 'remote', label: '远程', short: '远', icon: 'md-cloud' },
  { value: 'data_source', label: '数据源', short: '源', icon: 'md-git-network' },
  { value: 'rag', label: 'RAG', short: 'R', icon: 'md-pulse' },
];

/** 新建目录时可选择的数据类型（RAG 暂不可新建；文档目录下仅可选文档目录） */
export function getCreatableDirDataTypes(parentDir, allDirectories) {
  const underDoc = isDocumentDirectory(parentDir, allDirectories)
  return DIR_DATA_TYPES.filter((item) => {
    if (item.creatable === false) return false
    if (underDoc) return item.value === 'parsed'
    return true
  })
}

export function isDocumentDataType(dataType) {
  return normalizeDirDataType(dataType) === 'parsed'
}

/** 自身或祖先为文档目录（parsed）时，视为文档目录上下文 */
export function isDocumentDirectory(dir, allDirectories) {
  if (!dir) return false
  const contentDir = allDirectories
    ? resolveDirectoryContentRoot(dir, allDirectories)
    : dir
  if (!contentDir) return false
  if (!allDirectories) {
    return isDocumentDataType(contentDir.data_type)
  }
  const chain = getDirectoryAncestors(contentDir, allDirectories)
  return chain.some((node) => isDocumentDataType(node.data_type))
}

export function matchesDirViewFilter(dir, filter) {
  if (!dir || !filter || filter === 'all') return false;
  const type = normalizeDirType(dir.type);
  const dataType = normalizeDirDataType(dir.data_type);
  switch (filter) {
    case 'parsed':
      return type === 'dir' && dataType === 'parsed';
    case 'remote':
      return type === 'remote';
    case 'data_source':
      return type === 'dir' && dataType === 'data_source';
    case 'rag':
      return dataType === 'rag';
    default:
      return false;
  }
}

function pruneTreeByIds(nodes, keepIds) {
  return (nodes || []).map((node) => {
    const rawChildren = node.children || node.subs || [];
    const children = pruneTreeByIds(rawChildren, keepIds);
    const id = node.id != null ? String(node.id) : '';
    if ((id && keepIds.has(id)) || children.length) {
      return { ...node, children };
    }
    return null;
  }).filter(Boolean);
}

function pruneTreeByKeyword(nodes, keyword) {
  const kw = String(keyword || '').trim().toLowerCase();
  if (!kw) return nodes || [];
  return (nodes || []).map((node) => {
    const rawChildren = node.children || node.subs || [];
    const children = pruneTreeByKeyword(rawChildren, kw);
    const nameHit = String(node.name || '').toLowerCase().includes(kw);
    if (nameHit || children.length) {
      return { ...node, children };
    }
    return null;
  }).filter(Boolean);
}

export function filterDirectoryTree(nodes, { keyword = '', viewFilter = 'all' } = {}) {
  let tree = nodes || [];

  if (viewFilter && viewFilter !== 'all') {
    const keepIds = new Set();
    const walkView = (items, path) => {
      (items || []).forEach((node) => {
        const curPath = [...path, node];
        const rawChildren = node.children || node.subs || [];
        const isLeaf = !rawChildren.length;
        const matches = matchesDirViewFilter(node, viewFilter);
        if (viewFilter === 'remote' && matches) {
          curPath.forEach((n) => {
            if (n.id != null) keepIds.add(String(n.id));
          });
        } else if (isLeaf && matches) {
          curPath.forEach((n) => {
            if (n.id != null) keepIds.add(String(n.id));
          });
        }
        if (rawChildren.length) walkView(rawChildren, curPath);
      });
    };
    walkView(tree, []);
    tree = pruneTreeByIds(tree, keepIds);
  }

  if (keyword && String(keyword).trim()) {
    tree = pruneTreeByKeyword(tree, keyword);
  }

  return tree;
}

export function dedupeDirectoryFlat(list) {
  const map = new Map();
  (list || []).forEach((dir) => {
    if (dir && dir.id != null && !map.has(String(dir.id))) {
      map.set(String(dir.id), dir);
    }
  });
  return Array.from(map.values());
}

export function getDirDescription(dir) {
  if (!dir) return '';
  if (dir.description != null && String(dir.description).trim() !== '') return String(dir.description);
  if (dir.desc != null && String(dir.desc).trim() !== '') return String(dir.desc);
  return '';
}

export function compareDirName(a, b) {
  return String((a && a.name) || '').localeCompare(
    String((b && b.name) || ''),
    'zh-CN',
    { sensitivity: 'base', numeric: true },
  );
}

export function sortDirListByName(dirs) {
  return [...(dirs || [])].sort(compareDirName);
}

export function sortDirTree(nodes) {
  return sortDirListByName(nodes).map((node) => {
    const rawChildren = node.children || node.subs || [];
    return {
      ...node,
      children: rawChildren.length ? sortDirTree(rawChildren) : [],
    };
  });
}

export function isSystemDirectory(dir) {
  if (!dir) return false;
  return dir.system === true || normalizeDirType(dir.type) === 'system';
}

export function isRefDirectory(dir) {
  return normalizeDirType(dir && dir.type) === 'ref';
}

export function isRefDisplayDirectory(dir) {
  return !!(dir && dir._refDisplay);
}

export function isRefDirectoryContext(dir) {
  return isRefDirectory(dir) || isRefDisplayDirectory(dir);
}

export function markRefDisplayTree(nodes, underRef = true) {
  return (nodes || []).map((node) => ({
    ...node,
    _refDisplay: underRef,
    children: markRefDisplayTree(node.children || node.subs, underRef),
  }));
}

/** Vue :key for directory nodes; ref clones need a scope prefix to avoid duplicate keys */
export function getDirectoryVueKey(dir, { parentId = null, refList = false } = {}) {
  if (!dir || dir.id == null || dir.id === '') return 'dir-empty';
  const id = String(dir.id);
  if (refList || dir._refDisplay) {
    const scope = parentId != null && parentId !== '' ? String(parentId) : 'list';
    return `ref-${scope}-${id}`;
  }
  if (isRefDirectory(dir)) {
    return `ref-link-${id}`;
  }
  return `dir-${id}`;
}

export function getRefDirectoryTargetId(dir) {
  if (!isRefDirectory(dir)) return null;
  if (dir.ref_directory_id != null && dir.ref_directory_id !== '') {
    return String(dir.ref_directory_id);
  }
  if (dir.remote_api != null && String(dir.remote_api).trim() !== '') {
    return String(dir.remote_api).trim();
  }
  return null;
}

export function resolveDirectoryContentRoot(dir, allDirectories) {
  if (!dir) return null;
  const targetId = getRefDirectoryTargetId(dir);
  if (!targetId) return dir;
  return findDirectoryById(allDirectories, targetId) || dir;
}

export function isDirectoryContentEditable(dir, allDirectories) {
  if (!dir) return false;
  const contentDir = allDirectories
    ? resolveDirectoryContentRoot(dir, allDirectories)
    : dir;
  if (normalizeDirType(contentDir.type) === 'remote') return false;
  return contentDir.editable !== false;
}

/** 沿祖先链继承 enable_upload，默认允许；远程目录不允许上传 */
export function getEffectiveEnableUpload(dir, allDirectories) {
  if (!dir) return true;
  const contentDir = allDirectories
    ? resolveDirectoryContentRoot(dir, allDirectories)
    : dir;
  if (normalizeDirType(contentDir.type) === 'remote') return false;
  const chain = getDirectoryAncestors(contentDir, allDirectories).reverse();
  let enableUpload = true;
  chain.forEach((node) => {
    if (node.enable_upload === false) enableUpload = false;
    if (node.enable_upload === true) enableUpload = true;
  });
  return enableUpload;
}

export function isDirectoryUploadEnabled(dir, allDirectories) {
  return getEffectiveEnableUpload(dir, allDirectories);
}

export function getDirectoryIconMeta(dir, { isRoot = false, allDirectories } = {}) {
  if (isRoot) {
    return { icon: 'md-cube', color: 'var(--primary)', refBadge: false };
  }
  if (!dir) {
    return { icon: 'md-folder', color: '#faad14', refBadge: false };
  }
  if (isRefDirectory(dir)) {
    const target = resolveDirectoryContentRoot(dir, allDirectories);
    const targetMeta = getDirectoryIconMeta(
      target && !isRefDirectory(target) ? target : null,
      { allDirectories },
    );
    return { ...targetMeta, refBadge: true };
  }

  let meta = { icon: 'md-folder', color: '#faad14', refBadge: false };
  if (isSystemDirectory(dir)) {
    meta = { icon: 'md-lock', color: 'var(--primary)', refBadge: false };
  } else {
    const type = normalizeDirType(dir.type);
    const dataType = normalizeDirDataType(dir.data_type);
    if (type === 'remote') {
      meta = { icon: 'md-cloud', color: '#fa8c16', refBadge: false };
    } else if (dataType === 'parsed') {
      meta = { icon: 'md-document', color: '#096dd9', refBadge: false };
    } else if (dataType === 'data_source') {
      meta = { icon: 'md-git-network', color: '#13c2c2', refBadge: false };
    } else if (dataType === 'rag') {
      meta = { icon: 'md-pulse', color: '#531dab', refBadge: false };
    }
  }

  if (isRefDisplayDirectory(dir)) {
    return { ...meta, refBadge: true };
  }
  return meta;
}

export const PLATFORM_ADMIN_ROLE = {
  id: 'platform_admin',
  name: '平台管理员',
};

export function normalizeDirType(type) {
  if (type === 'link-dir') return 'ref';
  return type || 'dir';
}

export function normalizeDirDataType(dataType) {
  return dataType || 'raw';
}

export function getDirDataTypeOption(dataType) {
  const normalized = normalizeDirDataType(dataType);
  return DIR_DATA_TYPES.find(item => item.value === normalized) || null;
}

export function isLocalDirectory(dir) {
  return normalizeDirType(dir && dir.type) === 'dir';
}

export function isPlatformAdmin(session) {
  return !!(session && session.is_admin === true);
}

export function normalizeKnowledgeAuth(data) {
  const source = data || {};
  return {
    roles: Array.isArray(source.roles) ? source.roles : [],
    groups: Array.isArray(source.groups) ? source.groups : [],
  };
}

export function getKnowledgeRoleDisplay(session, knowledgeAuth) {
  if (isPlatformAdmin(session)) {
    return PLATFORM_ADMIN_ROLE.name;
  }
  const roles = (knowledgeAuth && knowledgeAuth.roles) || [];
  const names = roles
    .map((item) => (typeof item === 'object' ? item.name : item))
    .filter((name) => name != null && String(name).trim() !== '');
  return names.length ? names.join('、') : '普通用户';
}

export function parseAclExtra(extra) {
  if (!extra) return {};
  if (typeof extra === 'object') return extra;
  try {
    return JSON.parse(extra);
  } catch (e) {
    return {};
  }
}

export function getAclMod(acl) {
  const extra = parseAclExtra(acl && acl.extra);
  return extra.mod === 'edit' ? 'edit' : 'view';
}

function hasExplicitDirectoryOwnerId(dir) {
  return !!(dir && dir.owner_id != null && dir.owner_id !== '');
}

/** 单目录：有 owner_id 仅认 owner_id，否则才认 created_by */
export function getDirectoryOwnerId(dir) {
  if (!dir) return null;
  if (hasExplicitDirectoryOwnerId(dir)) return dir.owner_id;
  if (dir.created_by != null && dir.created_by !== '') return dir.created_by;
  return dir.charger_id || null;
}

/**
 * 有效负责人（权限判定）：沿祖先链继承 owner_id；
 * 整条链均无 owner_id 时才回退 created_by；一旦存在 owner_id 则 created_by 不生效。
 */
export function getEffectiveOwnerUserId(dir, allDirectories) {
  if (!dir) return null;
  const chain = getDirectoryAncestors(dir, allDirectories).reverse();
  let ownerId = null;
  chain.forEach((node) => {
    if (hasExplicitDirectoryOwnerId(node)) {
      ownerId = node.owner_id;
    }
  });
  if (ownerId != null && ownerId !== '') return ownerId;
  for (let i = chain.length - 1; i >= 0; i -= 1) {
    const node = chain[i];
    if (node.created_by != null && node.created_by !== '') return node.created_by;
  }
  return null;
}

/** 展示用负责人（含 charger_id 兜底） */
export function getEffectiveOwnerId(dir, allDirectories) {
  const userId = getEffectiveOwnerUserId(dir, allDirectories);
  if (userId != null && userId !== '') return userId;
  if (!dir) return null;
  return dir.charger_id || null;
}

export function isDirectoryOwner(session, dir, allDirectories) {
  if (!session || !dir) return false;
  const userId = session.id != null ? String(session.id) : '';
  if (!userId) return false;
  const ownerId = getEffectiveOwnerUserId(dir, allDirectories);
  return ownerId != null && String(ownerId) === userId;
}

export function getEffectiveIsShared(dir, allDirectories) {
  if (!dir) return false;
  const contentDir = allDirectories
    ? resolveDirectoryContentRoot(dir, allDirectories)
    : dir;
  const chain = getDirectoryAncestors(contentDir, allDirectories).reverse();
  let isShared = false;
  chain.forEach((node) => {
    if (node.is_shared === true) isShared = true;
    if (node.is_shared === false) isShared = false;
  });
  return isShared;
}

export function getEffectiveIsPrivate(dir, allDirectories) {
  if (!dir) return true;
  const contentDir = allDirectories
    ? resolveDirectoryContentRoot(dir, allDirectories)
    : dir;
  const chain = getDirectoryAncestors(contentDir, allDirectories).reverse();
  let isPrivate = true;
  chain.forEach((node) => {
    if (node.is_private === false) isPrivate = false;
    else if (node.is_private === true) isPrivate = true;
  });
  return isPrivate;
}

export function getEffectivePermissions(dir, allDirectories) {
  const chain = getDirectoryAncestors(dir, allDirectories).reverse();
  let permissions = [];
  chain.forEach((node) => {
    if (Array.isArray(node.permissions) && node.permissions.length > 0) {
      permissions = node.permissions;
    }
  });
  return permissions;
}

export function hasOwnPermissions(dir) {
  return Array.isArray(dir && dir.permissions) && dir.permissions.length > 0;
}

function sameId(a, b) {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
}

function findDirectoryById(allDirectories, id) {
  return (allDirectories || []).find(dir => sameId(dir.id, id)) || null;
}

export function getDirectoryAncestors(dir, allDirectories) {
  const chain = [];
  let cur = dir;
  while (cur) {
    chain.push(cur);
    if (cur.parent_id == null || cur.parent_id === undefined) break;
    cur = findDirectoryById(allDirectories, cur.parent_id);
  }
  return chain;
}

/** 树根目录（无 parent）；聚焦打开的展示根不算，仍按真实权限判定 */
export function isRootDirectory(dir) {
  if (!dir) return false;
  if (dir._focusRoot) return false;
  return dir.parent_id == null || dir.parent_id === undefined;
}

export function hasFullDirectoryAccess(session, dir, allDirectories, knowledgeAuth) {
  if (!session || !dir) return false;
  if (isPlatformAdmin(session)) return true;
  return isDirectoryOwner(session, dir, allDirectories);
}

function getUserGroupIds(knowledgeAuth) {
  return new Set(
    ((knowledgeAuth && knowledgeAuth.groups) || [])
      .map((item) => (typeof item === 'object' ? item.id : item))
      .filter((id) => id != null && id !== '')
      .map(String),
  );
}

function getUserRoleIds(knowledgeAuth) {
  return new Set(
    ((knowledgeAuth && knowledgeAuth.roles) || [])
      .map((item) => (typeof item === 'object' ? item.id : item))
      .filter((id) => id != null && id !== '')
      .map(String),
  );
}

function aclMatchesClient(acl, session, knowledgeAuth) {
  if (!acl || !session) return false;
  const clientType = acl.client_type;
  const clientId = acl.client_id != null ? String(acl.client_id) : '';
  if (!clientType || !clientId) return false;
  if (clientType === 'user') {
    return session.id != null && String(session.id) === clientId;
  }
  if (clientType === 'group') {
    return getUserGroupIds(knowledgeAuth).has(clientId);
  }
  if (clientType === 'role') {
    return getUserRoleIds(knowledgeAuth).has(clientId);
  }
  return false;
}

function getAclDecision(acls, session, knowledgeAuth, mod) {
  const matched = (acls || []).filter(
    (acl) => aclMatchesClient(acl, session, knowledgeAuth) && getAclMod(acl) === mod,
  );
  const denied = matched.some((acl) => !!acl.denied);
  if (denied) return false;
  if (matched.some((acl) => !acl.denied)) return true;
  return null;
}

export function canViewDirectory(session, dir, allDirectories, knowledgeAuth) {
  if (!dir) return false;
  // 1. 根目录不判定，始终可看（否则整树被裁光）
  if (isRootDirectory(dir)) return true;
  // 2. 个人目录（当前用户为负责人）始终可看
  if (isPersonalDirectory(session, dir, allDirectories)) return true;
  if (hasFullDirectoryAccess(session, dir, allDirectories, knowledgeAuth)) return true;
  // 3. 第三方：看目录权限（ACL + is_private）
  const perms = getEffectivePermissions(dir, allDirectories).map(normalizeAclRow);
  const decision = getAclDecision(perms, session, knowledgeAuth, 'view');
  // 显式 deny 一律禁止
  if (decision === false) return false;
  // 显式 allow
  if (decision === true) return true;
  // is_private=false：默认允许访问；is_private=true：需显式授权
  return !getEffectiveIsPrivate(dir, allDirectories);
}

/**
 * 从目录树中取出以 rootId 为根的子树（单根数组）。
 * 展示时将根节点 parent_id 置空，便于作为独立根节点打开。
 */
export function extractDirectorySubtree(nodes, rootId) {
  if (rootId == null || rootId === '') return nodes || [];
  let found = null;
  const walk = (items) => {
    (items || []).some((node) => {
      if (sameId(node.id, rootId)) {
        found = node;
        return true;
      }
      return walk(node.children || node.subs);
    });
  };
  walk(nodes);
  if (!found) return [];
  return [{
    ...found,
    parent_id: null,
    _focusRoot: true,
  }];
}

export function canEditDirectory(session, dir, allDirectories, knowledgeAuth) {
  if (!dir) return false;
  if (normalizeDirType(dir.type) === 'remote') return false;
  // 1. 根目录不判定（保持树可进入；配置入口仍由 UI 禁用）
  if (isRootDirectory(dir)) return true;
  // 2. 个人目录始终可编辑
  if (isPersonalDirectory(session, dir, allDirectories)) return true;
  if (dir.editable === false) return false;
  if (hasFullDirectoryAccess(session, dir, allDirectories, knowledgeAuth)) return true;
  // 3. 第三方：需目录 edit 授权
  const perms = getEffectivePermissions(dir, allDirectories).map(normalizeAclRow);
  const decision = getAclDecision(perms, session, knowledgeAuth, 'edit');
  return decision === true;
}

/**
 * 无权查看的目录整棵子树不展示（本节点及其子孙都不保留）。
 * 根目录始终保留，仅过滤其子项。
 */
export function filterViewableDirectoryTree(nodes, session, allDirectories, knowledgeAuth) {
  const walk = (items) => (items || []).map((node) => {
    const rawChildren = node.children || node.subs || [];
    if (isRootDirectory(node)) {
      return { ...node, children: walk(rawChildren) };
    }
    const contentDir = resolveDirectoryContentRoot(node, allDirectories) || node;
    if (!canViewDirectory(session, contentDir, allDirectories, knowledgeAuth)) {
      return null;
    }
    return { ...node, children: walk(rawChildren) };
  }).filter(Boolean);
  return walk(nodes);
}

/**
 * 编辑视图：仅保留有编辑权限的目录；为保持树结构，保留其祖先节点。
 * 调用前应已按查看权限过滤。
 */
export function filterEditableDirectoryTree(nodes, session, allDirectories, knowledgeAuth) {
  const walk = (items) => (items || []).map((node) => {
    const rawChildren = node.children || node.subs || [];
    const children = walk(rawChildren);
    const contentDir = resolveDirectoryContentRoot(node, allDirectories) || node;
    const editable = canEditDirectory(session, contentDir, allDirectories, knowledgeAuth);
    if (editable || children.length) {
      return { ...node, children };
    }
    return null;
  }).filter(Boolean);
  return walk(nodes);
}

export function canManageDirectory(session, dir, allDirectories, knowledgeAuth) {
  return hasFullDirectoryAccess(session, dir, allDirectories, knowledgeAuth);
}

/** 个人目录：当前用户为有效 owner（含未指定 owner 时的 created_by） */
export function isPersonalDirectory(session, dir, allDirectories) {
  if (!session || !dir) return false;
  const contentDir = allDirectories
    ? resolveDirectoryContentRoot(dir, allDirectories)
    : dir;
  return isDirectoryOwner(session, contentDir, allDirectories);
}

/** 目录名称着色：full 完全控制 | authorized 授权访问 | readonly 无写入 | system 系统目录 */
export function getDirectoryNameAccessLevel(session, dir, allDirectories, knowledgeAuth) {
  if (!dir) return 'readonly';
  if (isSystemDirectory(dir)) return 'system';
  if (isPersonalDirectory(session, dir, allDirectories)) return 'full';
  const contentDir = allDirectories
    ? resolveDirectoryContentRoot(dir, allDirectories)
    : dir;
  if (hasFullDirectoryAccess(session, contentDir, allDirectories, knowledgeAuth)) {
    return 'full';
  }
  if (canEditDirectory(session, contentDir, allDirectories, knowledgeAuth)) {
    return 'authorized';
  }
  return 'readonly';
}

export function normalizeAclRow(acl) {
  const extra = parseAclExtra(acl && acl.extra);
  return {
    id: acl.id,
    client_type: acl.client_type || 'user',
    client_id: acl.client_id != null ? String(acl.client_id) : '',
    target_id: acl.target_id,
    denied: !!acl.denied,
    mod: extra.mod === 'edit' ? 'edit' : 'view',
  };
}

export function buildAclPayload(directoryId, row) {
  const payload = {
    directory_id: directoryId,
    denied: !!row.denied,
    extra: { mod: row.mod === 'edit' ? 'edit' : 'view' },
  };
  const clientId = row.client_id != null ? String(row.client_id) : '';
  if (row.client_type === 'user') payload.user_id = clientId;
  else if (row.client_type === 'group') payload.group_id = clientId;
  else if (row.client_type === 'role') payload.role_id = clientId;
  return payload;
}

export function isRemoteDirectory(dir) {
  return normalizeDirType(dir && dir.type) === 'remote';
}

export function getAllowedContentTypes(dir, allDirectories) {
  const contentDir = allDirectories
    ? resolveDirectoryContentRoot(dir, allDirectories)
    : dir;
  // 文档目录及其子目录仅允许 Markdown
  if (isDocumentDirectory(contentDir, allDirectories)) return ['md'];
  const dataType = normalizeDirDataType(contentDir && contentDir.data_type);
  if (dataType === 'parsed') return ['md'];
  return ['file', 'text', 'md', 'html'];
}

const UPLOAD_EXT_MAP = {
  md: ['md', 'markdown'],
  html: ['html', 'htm'],
  text: ['txt', 'text'],
};

export function getFileExtFromName(name) {
  const base = (name || '').split(/[/\\]/).pop() || '';
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return '';
  return base.slice(dot + 1).toLowerCase();
}

/** 根据目录允许的内容类型，推断上传文件应创建的数据 type */
export function resolveUploadItemType(fileName, allowedTypes) {
  const allowed = allowedTypes || [];
  const ext = getFileExtFromName(fileName);
  if (!allowed.length) return null;

  if (allowed.length === 1 && allowed[0] === 'md') {
    return UPLOAD_EXT_MAP.md.includes(ext) ? 'md' : null;
  }

  for (const type of ['md', 'html', 'text']) {
    if (allowed.includes(type) && UPLOAD_EXT_MAP[type].includes(ext)) return type;
  }

  if (allowed.includes('file')) return 'file';
  return null;
}

export function getAllowedUploadAccept(allowedTypes) {
  const allowed = allowedTypes || [];
  if (allowed.includes('file')) return '';
  const parts = [];
  if (allowed.includes('md')) parts.push('.md', '.markdown');
  if (allowed.includes('html')) parts.push('.html', '.htm');
  if (allowed.includes('text')) parts.push('.txt', '.text');
  return parts.join(',');
}

export function getEmptyUploadLabel(allowedTypes) {
  const allowed = allowedTypes || [];
  if (allowed.length === 1 && allowed[0] === 'md') return '上传 Markdown';
  return '上传文件';
}

export function canUploadToDirectory(allowedTypes) {
  return (allowedTypes || []).length > 0;
}
